import React, { useState, useEffect, useCallback, useRef } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, addDoc, serverTimestamp, onSnapshot, getDoc } from '../../lib/auditedFirestore';
import { ref as dbRef, onValue, update, set, remove, onDisconnect } from 'firebase/database';
import { ref as sRef, uploadString, getDownloadURL } from 'firebase/storage';
import { db, rtdb, storage } from '../../lib/firebase';
import Spinner from '../../components/Spinner';
import CalculatorModal from '../../components/CalculatorModal';
import AdvancedWordProcessor from '../../components/editors/AdvancedWordProcessor';
import CustomSpreadsheet from '../../components/editors/CustomSpreadsheet';
import {spreadsheetToXlsx} from '../../components/editors/spreadsheetExport';
import StudentLectureNotes from './StudentLectureNotes';
import MaterialViewer from './MaterialViewer';
import LectureExamples from './LectureExamples';
import {withLectureNoteAccess,lectureNoteError} from './lectureNoteAccess';
import {materialSourceKey} from './materialAnnotationModel';
import {prepareMaterialAnnotationsForSave,resolveMaterialAnnotations} from './materialAnnotationStorage';
import HandwritingNotebook from '../../components/editors/HandwritingNotebook';
import {handNotesSVG} from '../../components/editors/handwritingModel';
import {prepareHandNotesForSave,resolveHandNotes} from './lectureNoteStorage';
import {flushEditorChanges} from '../../components/editors/useEditorFrame';
import {flushSync} from 'react-dom';
import * as XLSX from "xlsx";

const LectureRoom = ({ lecture, user, userRole = user?.role, onBack, mode = 'student' }) => { 
    // --- UI State ---
    const [noteTabs, setNoteTabs] = useState({ personal: 'word', live: 'word', admin: 'word' });
    const [viewMode, setViewMode] = useState('personal'); // 'personal' | 'live'
    const tabGroup = mode === 'student' ? viewMode : 'admin';
    const activeTab = noteTabs[tabGroup];
    const setActiveTab = tab => setNoteTabs(previous => ({ ...previous, [tabGroup]: tab }));
    const [showParticipants, setShowParticipants] = useState(false);
    const [showCalculator, setShowCalculator] = useState(false);
    const calculatorButton = useRef(null);
    const [searchQuery, setSearchQuery] = useState(''); 
    
    // --- Layout & Resize State ---
    const [leftPanelWidth, setLeftPanelWidth] = useState(50); // Default 50%
    const [showMaterial, setShowMaterial] = useState(true); // Toggle Material View
    const [isDragging, setIsDragging] = useState(false); // NEW: Fixes resize lagging
    const dividerRef = useRef(null);

    // --- Content State ---
    const [wordContent, setWordContent] = useState('');
    const [excelData, setExcelData] = useState(null);
    const [handNotes, setHandNotes] = useState(null);
    const [materialAnnotations,setMaterialAnnotations]=useState(null);
    const materialViewerRef=useRef(null);
    const materialDirty=useRef(false);
    const materialScope=useRef(null);materialScope.current={key:user.uid+':'+lecture.id,source:materialSourceKey(lecture)};
    const loadedNotesScope=useRef(null);
    const [saveError,setSaveError]=useState('');
    const [notesLoadError,setNotesLoadError]=useState('');
    const [notesRetry,setNotesRetry]=useState(0);
    const [isSaving, setIsSaving] = useState(false);
    
    // --- Live Session State ---
    const [liveWordContent, setLiveWordContent] = useState('');
    const [liveExcelData, setLiveExcelData] = useState(null);
    const [liveHandNotes,setLiveHandNotes]=useState(null);
    const [isLiveActive, setIsLiveActive] = useState(false);
    const [liveLoadError,setLiveLoadError]=useState('');
    const [liveRetry,setLiveRetry]=useState(0);
    const [onlineStudents, setOnlineStudents] = useState([]);
    
    // --- Work Completion State ---
    const [isWorkDone, setIsWorkDone] = useState(false);

    // --- Admin Review State ---
    const [reviewStudent, setReviewStudent] = useState(null); 
    const [reviewData, setReviewData] = useState(null); 
    const [isLoadingReview, setIsLoadingReview] = useState(false);
    const [reviewError,setReviewError]=useState('');

    // --- Metadata State ---
    const [isLoadingNotes, setIsLoadingNotes] = useState(true);

    // --- Import Notes State ---
    const [showImportModal, setShowImportModal] = useState(false);
    const [importableNotes, setImportableNotes] = useState([]);
    const [isFetchingImport, setIsFetchingImport] = useState(false);
    
    // --- Refs for Scroll Sync ---
    const viewerContainerRef = useRef(null);
    const scrollTimeoutRef = useRef(null);
    const viewModeRef = useRef(viewMode);
    viewModeRef.current = viewMode;

    const noteIds=useRef(new Map()),saveQueue=useRef(Promise.resolve()),saveTimer=useRef(null),latestNotes=useRef(null);
    latestNotes.current={wordContent,excelData,handNotes,materialAnnotations,userId:user.uid,lectureId:lecture.id,paperId:lecture.paperId || null,lectureTitle:lecture.title};

    // --- HYBRID STORAGE HELPER ---
    const resolveContent = async content => {
        if (typeof content === 'string' && content.startsWith('STORAGE_URL:')) {
            const response=await fetch(content.slice('STORAGE_URL:'.length),{cache:'no-store'});
            if(!response.ok)throw new Error('The saved Word notes could not be downloaded.');
            return response.text();
        }
        return content;
    };

    // --- 1. SAVE LOGIC ---
    const saveToFirestore = () => {
        const draft=latestNotes.current;
        if(!draft.userId || isLoadingNotes || notesLoadError || loadedNotesScope.current!==draft.userId+':'+draft.lectureId)return Promise.reject(new Error('Notes have not loaded yet.'));
        const key=draft.userId+':'+draft.lectureId;
        // Queue writes so older autosaves cannot overwrite newer notes or create duplicates.
        const task=saveQueue.current.catch(()=>{}).then(()=>withLectureNoteAccess(draft.userId,async()=>{
            const savedMaterial=await prepareMaterialAnnotationsForSave(draft,async (json,metadata)=>{
                const storageRef=sRef(storage,metadata.path);
                await uploadString(storageRef,json,'raw',{contentType:metadata.contentType,cacheControl:metadata.cacheControl});
                return getDownloadURL(storageRef);
            });
            const savedHandNotes=await prepareHandNotesForSave({...draft,materialAnnotations:savedMaterial},async json=>{
                const storageRef=sRef(storage,`lectureNotes/${draft.userId}/${draft.lectureId}_hand.json`);
                await uploadString(storageRef,json,'raw',{contentType:'application/json',cacheControl:'no-store'});
                return getDownloadURL(storageRef);
            });
            let finalWordContent=draft.wordContent;
            if(new Blob([JSON.stringify({...draft,handNotes:savedHandNotes,materialAnnotations:savedMaterial})]).size>850000 && draft.wordContent){
                const storageRef=sRef(storage,`lectureNotes/${draft.userId}/${draft.lectureId}_word.html`);
                await uploadString(storageRef,draft.wordContent,'raw',{contentType:'text/html'});
                finalWordContent=`STORAGE_URL:${await getDownloadURL(storageRef)}`;
            }
            const data={...draft,wordContent:finalWordContent,handNotes:savedHandNotes,materialAnnotations:savedMaterial,updatedAt:serverTimestamp()};
            const existing=noteIds.current.get(key);
            if(existing)await updateDoc(doc(db,'lectureNotes',existing),data);
            else {
                const saved=await addDoc(collection(db,'lectureNotes'),data);
                noteIds.current.set(key,saved.id);
            }
            if(latestNotes.current.userId+':'+latestNotes.current.lectureId===key && latestNotes.current.materialAnnotations===draft.materialAnnotations)materialDirty.current=false;
        }));
        saveQueue.current=task;
        return task;
    };
    const retrySave = async () => {
        flushSync(()=>{materialViewerRef.current?.flush();flushEditorChanges();});setIsSaving(true);setSaveError('');
        try{await saveToFirestore();}catch(error){setSaveError(lectureNoteError(error));}
        finally{setIsSaving(false);}
    };

    // --- 2. CLEANUP & EXIT LOGIC ---
    const handleExit = async () => {
        if(isLoadingNotes || notesLoadError){onBack();return;}
        flushSync(()=>{materialViewerRef.current?.flush();flushEditorChanges();});clearTimeout(saveTimer.current);setIsSaving(true);setSaveError('');
        try{await saveToFirestore();}catch(error){setSaveError(lectureNoteError(error));setIsSaving(false);return;}
        if (mode === 'admin-live') {
            setIsSaving(true);
            try {
                const sessionRef = dbRef(rtdb, `liveSessions/${lecture.id}`);
                await onDisconnect(sessionRef).cancel(); 
                await update(sessionRef, { isActive: false });
                const participantsRef = dbRef(rtdb, `liveSessions/${lecture.id}/participants`);
                await remove(participantsRef);
            } catch (error) {
                console.error("Error stopping live session:", error);
            }
        } else if (mode === 'student') {
            try {
                const myPresenceRef = dbRef(rtdb, `liveSessions/${lecture.id}/participants/${user.uid}`);
                await remove(myPresenceRef);
            } catch (e) {
                console.error("Error removing presence", e);
            }
        }
        onBack();
    };

    useEffect(()=>{
        const protect=event=>{
            flushSync(()=>materialViewerRef.current?.flush());
            if(materialDirty.current){event.preventDefault();event.returnValue='';}
        };
        window.addEventListener('beforeunload',protect);
        return()=>window.removeEventListener('beforeunload',protect);
    },[]);

    // --- 3. PRESENCE & LIVE DATA SYNC ---
    useEffect(() => {
        const sessionRef = dbRef(rtdb, `liveSessions/${lecture.id}`);
        
        if (mode === 'admin-live') {
            update(sessionRef, { isActive: true });
            onDisconnect(sessionRef).update({ isActive: false });
        }

        if (mode === 'student') {
            const myPresenceRef = dbRef(rtdb, `liveSessions/${lecture.id}/participants/${user.uid}`);
            
            const establishPresence = async () => {
                let displayName = user.name || user.displayName;

                if (!displayName) {
                    try {
                        const userDocRef = doc(db, "users", user.uid);
                        const userDocSnap = await getDoc(userDocRef);
                        if (userDocSnap.exists()) {
                            const userData = userDocSnap.data();
                            displayName = userData.name || userData.fullName || userData.username;
                        }
                    } catch (error) {
                        console.error("Error fetching name:", error);
                    }
                }

                if (!displayName) displayName = user.email ? user.email.split('@')[0] : "Student";

                await set(myPresenceRef, {
                    name: displayName,
                    email: user.email,
                    joinedAt: Date.now(),
                    isOnline: true,
                    isDone: false 
                });

                onDisconnect(myPresenceRef).remove();
            };

            establishPresence().catch(error=>console.warn("Lecture presence unavailable:", error.code || "connection-failed"));

            return () => {
                Promise.resolve(remove(myPresenceRef)).catch(()=>{});
                if (mode === 'admin-live') {
                     update(sessionRef, { isActive: false });
                     onDisconnect(sessionRef).cancel();
                }
            };
        }

        return () => {
            if (mode === 'admin-live') {
                update(sessionRef, { isActive: false });
                onDisconnect(sessionRef).cancel();
            }
        };
    }, [mode, lecture.id, user.uid, user.displayName, user.email, user.name]);

    // --- 4. TOGGLE DONE (Student) ---
    const toggleWorkDone = async () => {
        if (mode !== 'student') return;
        
        const newStatus = !isWorkDone;
        setIsWorkDone(newStatus);
        
        try {
            const myPresenceRef = dbRef(rtdb, `liveSessions/${lecture.id}/participants/${user.uid}`);
            await update(myPresenceRef, { isDone: newStatus });
        } catch (e) {
            console.error("Error updating done status:", e);
            setIsWorkDone(!newStatus); 
        }
    };

    // --- 5. FETCH NOTES & LIVE DATA LISTENERS ---
    useEffect(() => {
        let active=true;
        setIsLoadingNotes(true);setNotesLoadError('');setSaveError('');loadedNotesScope.current=null;
        setMaterialAnnotations(null);
        const fetchNotes = async () => {
            try {
                const q = query(
                    collection(db, "lectureNotes"), 
                    where("userId", "==", user.uid), 
                    where("lectureId", "==", lecture.id)
                );
                const snapshot = await withLectureNoteAccess(user.uid,()=>getDocs(q));
                if(!active)return;
                let loadedWord = '';
                let loadedExcel = null;
                let loadedHandNotes=null,loadedMaterial=null;

                if (!snapshot.empty) {
                    const data = snapshot.docs[0].data();
                    noteIds.current.set(user.uid+':'+lecture.id,snapshot.docs[0].id);
                    loadedWord = await resolveContent(data.wordContent || '');
                    loadedExcel = data.excelData || null;
                    loadedHandNotes=await resolveHandNotes(data.handNotes);
                    loadedMaterial=await resolveMaterialAnnotations(data.materialAnnotations);

                }

                if(!active)return;
                loadedNotesScope.current=user.uid+':'+lecture.id;materialDirty.current=false;
                setWordContent(loadedWord);setExcelData(loadedExcel);setHandNotes(loadedHandNotes);setMaterialAnnotations(loadedMaterial);
                if (mode === 'admin-live') {
                    const sessionRef = dbRef(rtdb, `liveSessions/${lecture.id}`);
                    update(sessionRef, { 
                        wordContent: loadedWord,
                        excelData: loadedExcel,
                        handNotes: loadedHandNotes,
                        isActive: true,
                        lastUpdated: Date.now()
                    });
                }
            } catch (error) {
                console.error("Error fetching notes:", error);if(active)setNotesLoadError(lectureNoteError(error,true));
            } finally {
                if(active)setIsLoadingNotes(false);
            }
        };

        fetchNotes();
        if (mode === 'admin-live') setViewMode('live');
        return()=>{active=false;};
    }, [lecture.id, user.uid, mode, notesRetry]);

    // Admin Review Listener
    useEffect(() => {
        if (!reviewStudent || mode !== 'admin-live') {
            setReviewData(null);
            return;
        }
        setIsLoadingReview(true);setReviewError('');let active=true,revision=0;
        const q = query(
            collection(db, "lectureNotes"), 
            where("userId", "==", reviewStudent.uid), 
            where("lectureId", "==", lecture.id)
        );
        const unsubscribe = onSnapshot(q, async (snapshot) => {
            const current=++revision;
            try {
            if (!snapshot.empty) {
                const data = snapshot.docs[0].data();
                const resolvedWord = await resolveContent(data.wordContent || '');
                const resolvedHand=await resolveHandNotes(data.handNotes);
                if(!active || current!==revision)return;
                setReviewData({
                    word: resolvedWord,
                    excel: data.excelData || null,
                    hand: resolvedHand
                });
            } else {
                setReviewData({ word: '', excel: null,hand:null }); 
            }
            setReviewError('');
            } catch(error){if(!active || current!==revision)return;setReviewError('Could not load this student’s notes. Close Review and try again.');setReviewData(null);}
            setIsLoadingReview(false);
        },()=>{if(active){setReviewError('Could not load this student’s notes. Close Review and try again.');setIsLoadingReview(false);}});
        return () => {active=false;unsubscribe();};
    }, [reviewStudent, lecture.id, mode]);

    // Private notes keep saving even while the student reads the faculty board.
    useEffect(() => {
        if (isLoadingNotes || notesLoadError || reviewStudent) return;
        let active=true;
        setIsSaving(true);
        saveTimer.current=setTimeout(async()=>{
            try { await saveToFirestore();if(active)setSaveError(''); }
            catch(error) { if(active)setSaveError(lectureNoteError(error)); }
            finally { if(active)setIsSaving(false); }
        },2000);
        return()=>{active=false;clearTimeout(saveTimer.current);};
        // saveToFirestore snapshots latestNotes; content changes schedule the next save.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[wordContent,excelData,handNotes,materialAnnotations,user.uid,lecture.id,isLoadingNotes,notesLoadError,mode,reviewStudent]);

    // Live Session Sync & Scroll
    const updateLiveSession = useCallback(async (updates) => {
        if (mode !== 'admin-live') return;
        const sessionRef = dbRef(rtdb, `liveSessions/${lecture.id}`);
        try {
            await update(sessionRef, { ...updates, isActive: true, lastUpdated: Date.now() });
        } catch (e) {
            console.error("Error broadcasting:", e);
        }
    }, [mode, lecture.id]);

    const handleScroll = () => {
        if (mode === 'admin-live' && viewerContainerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = viewerContainerRef.current;
            const scrollPercent = scrollHeight>clientHeight ? (scrollTop / (scrollHeight - clientHeight)) * 100 : 0;
            if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
            scrollTimeoutRef.current = setTimeout(() => {
                updateLiveSession({ scrollPercent });
            }, 50); 
        }
    };

    useEffect(() => {
        const sessionRef = dbRef(rtdb, `liveSessions/${lecture.id}`);
        let active = true;
        let revision = 0;
        const unsubscribeSession = onValue(sessionRef, async (snapshot) => {
            const current = ++revision;
            const data = snapshot.val();
            if (data) {
                setIsLiveActive(data.isActive===true);
                try {
                const resolvedLiveWord = await resolveContent(data.wordContent || '');
                if (!active || current !== revision) return;
                setLiveWordContent(resolvedLiveWord);
                setLiveExcelData(data.excelData || null);
                setLiveHandNotes(data.handNotes || null);setLiveLoadError('');
                setIsLiveActive(data.isActive === true);
                if (data.isActive === true && mode === 'student' && viewModeRef.current === 'live' && viewerContainerRef.current && data.scrollPercent !== undefined) {
                    const { scrollHeight, clientHeight } = viewerContainerRef.current;
                    const targetScrollTop = (data.scrollPercent / 100) * (scrollHeight - clientHeight);
                    viewerContainerRef.current.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
                }
                }catch{if(active && current===revision)setLiveLoadError('Faculty notes could not load. Your private notes are safe.');}
            } else {
                setIsLiveActive(false);setLiveLoadError('');
            }
        },()=>{if(active)setLiveLoadError('The live notes connection was denied or interrupted. Your private notes are safe.');});

        let unsubscribeParticipants = () => {};
        if (mode === 'admin-live') {
            const participantsRef = dbRef(rtdb, `liveSessions/${lecture.id}/participants`);
            unsubscribeParticipants = onValue(participantsRef, (snapshot) => {
                const data = snapshot.val();
                if (data) {
                    const students = Object.entries(data).map(([uid, info]) => ({ uid, ...info }));
                    setOnlineStudents(students);
                } else {
                    setOnlineStudents([]);
                }
            });
        }
        return () => {
            active = false;
            unsubscribeSession();
            unsubscribeParticipants();
        };
    }, [lecture.id, mode,liveRetry]);

    const handleAdminContentChange = (type, content) => {
        if (mode !== 'admin-live') return;
        if (reviewStudent) return; 
        if (type === 'word') {
            setWordContent(content); 
            updateLiveSession({ wordContent: content });
        } else if(type==='hand'){
            setHandNotes(content);updateLiveSession({handNotes:content});
        } else {
            setExcelData(content); 
            updateLiveSession({ excelData: content });
        }
    };

    // --- 6. RESIZE, EXPORT, & IMPORT LOGIC ---
    useEffect(() => {
        const divider = dividerRef.current;
        if (!divider) return;
        
        const handleMouseDown = (e) => {
            e.preventDefault();
            setIsDragging(true); // Engages dragging state
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        };
        
        const handleMouseMove = (e) => {
            // Instant calculation without transition delays
            const newWidth = (e.clientX / window.innerWidth) * 100;
            if (newWidth > 15 && newWidth < 85) setLeftPanelWidth(newWidth);
        };
        
        const handleMouseUp = () => {
            setIsDragging(false); // Disengages dragging state
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'default';
            document.body.style.userSelect = 'auto';
        };
        
        divider.addEventListener('mousedown', handleMouseDown);
        return () => {
            divider.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [showMaterial]); 

    const handleExport = () => {
        flushSync(()=>{materialViewerRef.current?.flush();flushEditorChanges();});
        if(activeTab==='hand'){
            const notes=reviewStudent?reviewData?.hand:mode==='admin-live' || viewMode==='personal'?latestNotes.current.handNotes:liveHandNotes;
            const url=URL.createObjectURL(new Blob([handNotesSVG(notes)],{type:'image/svg+xml'}));
            const link=document.createElement('a');link.href=url;link.download='Instil-hand-notes.svg';document.body.append(link);link.click();link.remove();setTimeout(()=>URL.revokeObjectURL(url),1000);return;
        }
        if (activeTab === 'word') {
            const noteContent = reviewStudent?reviewData?.word:mode==='admin-live' || viewMode==='personal'?latestNotes.current.wordContent:liveWordContent;
            if (!noteContent) { alert("No content to export."); return; }
            const header = "<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'><head><meta charset='utf-8'><title>Lecture Notes</title></head><body>";
            const footer = "</body></html>";
            const sourceHTML = header + noteContent + footer;
            const source = 'data:application/vnd.ms-word;charset=utf-8,' + encodeURIComponent(sourceHTML);
            const fileDownload = document.createElement("a");
            document.body.appendChild(fileDownload);
            fileDownload.href = source;
            fileDownload.download = `Lecture_Notes_${lecture.title}.doc`;
            fileDownload.click();
            document.body.removeChild(fileDownload);
        } else if (activeTab === 'excel') {
            const sheetData = reviewStudent?reviewData?.excel:mode==='admin-live' || viewMode==='personal'?latestNotes.current.excelData:liveExcelData;
            if (!sheetData) { alert("No data to export."); return; }
            const wb = spreadsheetToXlsx(sheetData);
            XLSX.writeFile(wb, `Lecture_Spreadsheet_${lecture.title}.xlsx`);
        }
    };

    const openImportModal = async () => {
        setShowImportModal(true);
        setIsFetchingImport(true);
        try {
            const q = query(collection(db, "lectureNotes"), where("userId", "==", user.uid));
            const snapshot = await withLectureNoteAccess(user.uid,()=>getDocs(q));
            const notes = [];
            snapshot.forEach((docSnap) => {
                const data = docSnap.data();
                if (data.lectureId !== lecture.id) {
                    notes.push({ id: docSnap.id, ...data });
                }
            });
            notes.sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0));
            setImportableNotes(notes);
        } catch (error) {
            console.error("Failed to fetch importable notes", error);
        } finally {
            setIsFetchingImport(false);
        }
    };

    const handleImportNote = async (selectedNote) => {
        const confirmMsg = "This will replace your current Word, Spreadsheet and Hand Notes with the imported ones. Do you want to continue?";
        if (!window.confirm(confirmMsg)) return;

        setShowImportModal(false);
        setIsLoadingNotes(true);

        try {
            const resolvedWord = await resolveContent(selectedNote.wordContent || '');
            const resolvedExcel = selectedNote.excelData || null;
            const resolvedHand=await resolveHandNotes(selectedNote.handNotes);

            setWordContent(resolvedWord);
            setExcelData(resolvedExcel);
            setHandNotes(resolvedHand);

            if (mode === 'admin-live') {
                updateLiveSession({ wordContent: resolvedWord, excelData: resolvedExcel,handNotes:resolvedHand });
            }
        } catch (error) {
            console.error("Failed to import selected note", error);
            window.alert("Could not import the saved notes. Your current notes have not been replaced. Please try again.");
        } finally {
            setIsLoadingNotes(false);
        }
    };

    // --- 7. RENDER ---
    const filteredStudents = onlineStudents.filter(stu => 
        (stu.name && stu.name.toLowerCase().includes(searchQuery.toLowerCase())) || 
        (stu.email && stu.email.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    let displayWord, displayExcel,displayHand;
    if (reviewStudent && reviewData) {
        displayWord = reviewData.word;
        displayExcel = reviewData.excel;displayHand=reviewData.hand;
    } else if (mode === 'admin-live') {
        displayWord = wordContent;
        displayExcel = excelData;displayHand=handNotes;
    } else if (viewMode === 'live') {
        displayWord = liveWordContent;
        displayExcel = liveExcelData;displayHand=liveHandNotes;
    } else {
        displayWord = wordContent;
        displayExcel = excelData;displayHand=handNotes;
    }

    const isReadOnly = (mode === 'student' && viewMode === 'live') || (mode === 'admin-live' && !!reviewStudent);

    const handleMaterialChange=(value,source)=>{
        if(loadedNotesScope.current!==user.uid+':'+lecture.id || materialScope.current.key!==user.uid+':'+lecture.id || materialScope.current.source!==source || notesLoadError)return;
        materialDirty.current=true;latestNotes.current={...latestNotes.current,materialAnnotations:value};setMaterialAnnotations(value);
    };


    return (
        <div className="fixed inset-0 bg-gray-100 flex flex-col z-50">
            {/* Transparent overlay blocks iframes from capturing mouse events during resize */}
            {isDragging && <div className="fixed inset-0 z-[100] cursor-col-resize select-none" />}

            {/* Header */}
            <header className="h-16 bg-white border-b flex items-center justify-between px-6 shadow-sm flex-shrink-0 z-20 relative">
                <div className="flex items-center gap-4">
                    <button onClick={handleExit} className="text-gray-600 hover:text-purple-600 font-bold flex items-center gap-2 transition">&larr; Exit</button>
                    <div className="h-6 w-px bg-gray-300"></div>
                    <button onClick={() => setShowMaterial(!showMaterial)} className={`text-xs font-bold px-3 py-1.5 rounded border transition ${!showMaterial ? 'bg-purple-100 text-purple-700 border-purple-300' : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'}`}>{showMaterial ? 'Hide Material' : 'Show Material'}</button>
                    <div className="h-6 w-px bg-gray-300"></div>
                    <div>
                        <h1 className="font-bold text-lg text-gray-800 flex items-center gap-2">
                            {lecture.title}
                            {mode === 'admin-live' && <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded animate-pulse">LIVE BROADCAST</span>}
                            {mode === 'student' && isLiveActive && <span className="bg-red-100 text-red-600 border border-red-200 text-xs px-2 py-0.5 rounded font-bold">LIVE NOW</span>}
                        </h1>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    <button ref={calculatorButton} type="button" aria-expanded={showCalculator} aria-controls="lecture-calculator" onClick={() => setShowCalculator(value => !value)} className="bg-purple-50 border border-purple-200 text-purple-700 px-3 py-1.5 rounded text-sm font-bold hover:bg-purple-100 transition">Calculator</button>
                    {mode === 'student' && (
                        <button 
                            onClick={toggleWorkDone}
                            className={`px-3 py-1.5 rounded-md text-sm font-bold flex items-center gap-2 transition border ${isWorkDone ? 'bg-green-100 text-green-700 border-green-300' : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'}`}
                        >
                            <span>{isWorkDone ? '✅' : '⬜'}</span>
                            {isWorkDone ? 'Done' : 'Mark as Done'}
                        </button>
                    )}

                    {(!isReadOnly) && (
                        <button onClick={openImportModal} className="bg-blue-50 border border-blue-200 text-blue-700 px-3 py-1.5 rounded text-sm font-bold flex items-center gap-2 hover:bg-blue-100 transition" title="Import notes from another paper"><span>📥</span> Import</button>
                    )}
                    <button onClick={handleExport} className="bg-indigo-50 border border-indigo-200 text-indigo-700 px-3 py-1.5 rounded text-sm font-bold flex items-center gap-2 hover:bg-indigo-100 transition" title="Export"><span>⬇️</span> Export</button>
                    
                    <div className="h-6 w-px bg-gray-300"></div>

                    {mode === 'admin-live' && (
                        <div className="relative">
                            <button onClick={() => { setShowParticipants(!showParticipants); if(!showParticipants) setSearchQuery(''); }} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-bold border transition ${showParticipants ? 'bg-purple-100 border-purple-200 text-purple-700' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'}`}>
                                <span>👥 Students</span>
                                <span className={`px-1.5 rounded-full text-xs ${onlineStudents.length > 0 ? 'bg-green-500 text-white' : 'bg-gray-400 text-white'}`}>{onlineStudents.length}</span>
                            </button>
                            {showParticipants && (
                                <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-50">
                                    <div className="bg-gray-50 px-4 py-2 border-b flex justify-between items-center"><h3 className="font-bold text-gray-700 text-sm">Online Students</h3><span className="text-xs text-green-600 font-medium bg-green-100 px-2 py-0.5 rounded-full">Live</span></div>
                                    <div className="px-3 py-2 border-b bg-white">
                                        <div className="relative"><input type="text" placeholder="Search student..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-8 pr-3 py-1.5 text-sm border rounded bg-gray-50 focus:bg-white focus:ring-2 focus:ring-purple-400 outline-none transition" autoFocus /><span className="absolute left-2.5 top-1.5 text-gray-400 text-sm">🔍</span></div>
                                    </div>
                                    <div className="max-h-64 overflow-y-auto">
                                        {onlineStudents.length === 0 ? <p className="p-4 text-center text-gray-400 text-sm italic">No students joined yet.</p> : filteredStudents.length === 0 ? <p className="p-4 text-center text-gray-400 text-sm">No match found.</p> : (
                                            <ul className="divide-y divide-gray-100">
                                                {filteredStudents.map((stu) => (
                                                    <li key={stu.uid} onClick={() => { setReviewStudent(stu); setShowParticipants(false); }} className="px-4 py-3 hover:bg-purple-50 cursor-pointer flex items-center gap-3 transition group">
                                                        <div className="h-8 w-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center font-bold text-xs group-hover:bg-purple-200">{(stu.name || "?").charAt(0).toUpperCase()}</div>
                                                        <div className="overflow-hidden flex-1"><p className="text-sm font-bold text-gray-800 truncate group-hover:text-purple-700">{stu.name || "Unknown"}</p><p className="text-xs text-gray-500 truncate">{stu.email}</p></div>
                                                        {stu.isDone && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-bold border border-green-200">Done</span>}
                                                        <div className="text-xs text-gray-400 group-hover:text-purple-600 font-semibold">Review</div>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {(viewMode === 'personal' || mode === 'admin-live') && !reviewStudent && (
                        saveError || notesLoadError ? <span className="text-sm text-red-600">Not saved</span> : isSaving ? <span className="text-sm text-gray-500 flex items-center gap-2"><Spinner color="border-gray-500" /> Saving...</span> : <span className="text-sm text-green-600 font-medium flex items-center gap-1">✅ Saved</span>
                    )}
                </div>
            </header>

            {mode==='admin-live' && ['admin','superadmin'].includes(userRole) && <LectureExamples key={`${user.uid}:${lecture.id}`} lecture={lecture} user={user} role={userRole} faculty disabled={isLoadingNotes || Boolean(notesLoadError) || Boolean(reviewStudent)}/>}
            {(saveError || notesLoadError) && <div role="alert" className="bg-red-50 text-red-700 px-4 py-2 text-sm flex items-center gap-3">{notesLoadError || saveError}{notesLoadError?<button onClick={()=>setNotesRetry(value=>value+1)} className="font-bold underline">Retry loading notes</button>:<button onClick={retrySave} className="font-bold underline">Retry saving</button>}</div>}
            {liveLoadError && mode==='student' && viewMode==='live' && <div role="alert" className="bg-amber-50 text-amber-900 px-4 py-2 text-sm">{liveLoadError} <button type="button" className="font-bold underline" onClick={()=>setLiveRetry(value=>value+1)}>Retry live notes</button></div>}
            <div className="flex-1 flex overflow-hidden">
                
                {/* Left Panel - Material Viewer */}
                {/* Notice the conditional removal of 'transition-all' when dragging */}
                <div style={{ width: showMaterial ? `${leftPanelWidth}%` : '0px', display: showMaterial ? 'flex' : 'none' }} className={`bg-gray-200 border-r border-gray-300 flex-col relative ${isDragging ? '' : 'transition-all duration-300 ease-in-out'}`}>
                    <div className="flex-1 p-4 overflow-hidden">
                        <MaterialViewer key={`${user.uid}:${lecture.id}:${materialSourceKey(lecture)}`} ref={materialViewerRef} lecture={lecture} annotations={materialAnnotations} onChange={handleMaterialChange} scrollRef={viewerContainerRef} onScroll={handleScroll} readOnly={isLoadingNotes || Boolean(notesLoadError) || Boolean(reviewStudent)} saveStatus={notesLoadError?'Saved notes unavailable':saveError?'Not saved — retry saving':isSaving?'Saving…':'Saved with your notes'}/>
                    </div>
                    {mode === 'admin-live' && <p className="text-xs text-center text-gray-500 pb-1">Scroll here to sync student view</p>}
                </div>
                
                {/* Modern Resizable Divider */}
                {showMaterial && (
                    <div 
                        ref={dividerRef} 
                        className={`w-3 hover:w-3 cursor-col-resize z-30 flex items-center justify-center transition-colors shadow-[inset_1px_0_2px_rgba(0,0,0,0.05)] group ${isDragging ? 'bg-purple-500' : 'bg-gray-200 hover:bg-purple-300'}`}
                        title="Drag to resize"
                    >
                        <div className={`flex flex-col gap-1.5 transition-opacity ${isDragging ? 'opacity-100' : 'opacity-40 group-hover:opacity-100'}`}>
                            <div className={`w-1 h-1 rounded-full ${isDragging ? 'bg-white' : 'bg-gray-500 group-hover:bg-white'}`}></div>
                            <div className={`w-1 h-1 rounded-full ${isDragging ? 'bg-white' : 'bg-gray-500 group-hover:bg-white'}`}></div>
                            <div className={`w-1 h-1 rounded-full ${isDragging ? 'bg-white' : 'bg-gray-500 group-hover:bg-white'}`}></div>
                        </div>
                    </div>
                )}
                
                {/* Right Panel - Editors */}
                {/* Notice the conditional removal of 'transition-all' when dragging */}
                <div style={{ width: showMaterial ? `${100 - leftPanelWidth}%` : '100%' }} className={`flex flex-col bg-white ${isDragging ? '' : 'transition-all duration-300 ease-in-out'}`}>
                    {mode === 'student' && (
                        <div className="flex border-b bg-gray-100 p-1 gap-2">
                            <button onClick={() => setViewMode('personal')} className={`flex-1 py-2 text-sm font-bold rounded-md transition ${viewMode === 'personal' ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-500 hover:bg-gray-200'}`}>📝 My Private Notes</button>
                            <button onClick={() => setViewMode('live')} className={`flex-1 py-2 text-sm font-bold rounded-md transition flex items-center justify-center gap-2 ${viewMode === 'live' ? 'bg-red-50 text-red-600 shadow-sm border border-red-100' : 'text-gray-500 hover:bg-gray-200'}`}>{isLiveActive ? '🔴 Live Class Board' : 'Live Class (Offline)'}</button>
                        </div>
                    )}
                    {reviewStudent && (
                        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2 flex items-center justify-between">
                            <div className="flex items-center gap-2"><span className="text-xl">👀</span><div><p className="text-sm font-bold text-yellow-800">Reviewing: {reviewStudent.name}</p><p className="text-xs text-yellow-600">Read-only mode.</p></div></div>
                            <button onClick={() => setReviewStudent(null)} className="bg-white border border-yellow-300 text-yellow-700 text-xs font-bold px-3 py-1 rounded hover:bg-yellow-100">Close Review</button>
                        </div>
                    )}
                    <div className="flex border-b bg-gray-50">
                        <button onClick={() => setActiveTab('word')} className={`flex-1 py-3 text-sm font-bold flex items-center justify-center gap-2 transition ${activeTab === 'word' ? 'border-b-2 border-purple-600 text-purple-700 bg-white' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'}`}><span>📄</span> Word Notes</button>
                        <button onClick={() => setActiveTab('hand')} className={`flex-1 py-3 text-sm font-bold flex items-center justify-center gap-2 transition ${activeTab === 'hand' ? 'border-b-2 border-purple-600 text-purple-700 bg-white' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'}`}><span>✎</span> Hand Notes</button>
                        <button onClick={() => setActiveTab('excel')} className={`flex-1 py-3 text-sm font-bold flex items-center justify-center gap-2 transition ${activeTab === 'excel' ? 'border-b-2 border-purple-600 text-purple-700 bg-white' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'}`}><span>📊</span> Spreadsheet</button>
                    </div>                    
                    <div className="flex-1 overflow-hidden relative bg-white">
                        {((notesLoadError && !(mode==='student' && viewMode==='live')) || (reviewStudent && reviewError))?<p role={reviewStudent && reviewError?"alert":undefined} className="p-6 text-sm text-red-600">{notesLoadError || reviewError}</p>:(isLoadingNotes && !(mode==='student' && viewMode==='live')) || (reviewStudent && isLoadingReview) ? (
                            <div className="absolute inset-0 flex items-center justify-center"><Spinner color="border-purple-500" /></div>
                        ) : (
                            <>
                                {isReadOnly && !reviewStudent && <div className="absolute top-0 right-0 z-10 p-2 pointer-events-none"><span className="bg-red-100 text-red-600 text-xs font-bold px-2 py-1 rounded border border-red-200 opacity-90 shadow-sm">VIEWING INSTRUCTOR SCREEN</span></div>}
                                {mode === 'student' ? <StudentLectureNotes key={`${lecture.id}:${user.uid}`} viewMode={viewMode} activeTab={activeTab}
                                    wordContent={wordContent} excelData={excelData} handNotes={handNotes} liveWordContent={liveWordContent} liveExcelData={liveExcelData} liveHandNotes={liveHandNotes}
                                    onWordChange={setWordContent} onExcelChange={setExcelData} onHandNotesChange={setHandNotes}/> : <>
                                <div className={`absolute inset-0 flex flex-col ${activeTab === 'word' ? 'block' : 'hidden'}`}>
                                    <AdvancedWordProcessor key={`word-${viewMode}-${reviewStudent?.uid}`} value={displayWord} readOnly={isReadOnly} onChange={(val) => { if (mode === 'admin-live' && !reviewStudent) handleAdminContentChange('word', val); else if (viewMode === 'personal' && !reviewStudent) setWordContent(val); }} />
                                </div>
                                <div className={`absolute inset-0 flex flex-col ${activeTab === 'excel' ? 'block' : 'hidden'}`}>
                                    <CustomSpreadsheet key={`excel-${viewMode}-${reviewStudent?.uid}`} data={displayExcel} editableCells={isReadOnly?[]:undefined} onChange={(val) => { if (mode === 'admin-live' && !reviewStudent) handleAdminContentChange('excel', val); else if (viewMode === 'personal' && !reviewStudent) setExcelData(val); }} />
                                </div>
                                <section aria-label="Faculty hand notes" aria-hidden={activeTab!=='hand'} inert={activeTab!=='hand'} className="absolute inset-0 flex flex-col" style={{visibility:activeTab==='hand'?'visible':'hidden',pointerEvents:activeTab==='hand'?'auto':'none'}}>
                                    <HandwritingNotebook key={`hand-${lecture.id}-${reviewStudent?.uid || 'faculty'}`} data={displayHand} readOnly={isReadOnly} preserveViewport={isReadOnly} onChange={val=>{if(mode==='admin-live' && !reviewStudent)handleAdminContentChange('hand',val);}}/>
                                </section>
                                </>}
                            </>
                        )}
                    </div>              
                </div>
            </div>

            {/* Keep calculations when minimised or when changing the notes tab. */}
            <div id="lecture-calculator" hidden={!showCalculator}>
                <CalculatorModal floating onClose={() => {setShowCalculator(false);calculatorButton.current?.focus();}}/>
            </div>
            {/* Import Notes Modal */}
            {showImportModal && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[100] px-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col max-h-[80vh]">
                        <div className="p-4 border-b flex justify-between items-center">
                            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><span>📥</span> Import Notes</h2>
                            <button onClick={() => setShowImportModal(false)} className="text-gray-500 hover:text-gray-800 text-xl font-bold">&times;</button>
                        </div>
                        <div className="p-4 flex-1 overflow-y-auto">
                            {isFetchingImport ? (
                                <div className="flex flex-col items-center py-10 gap-3">
                                    <Spinner color="border-blue-500" />
                                    <p className="text-sm text-gray-500">Loading your saved notes...</p>
                                </div>
                            ) : importableNotes.length === 0 ? (
                                <p className="text-center text-gray-500 py-6">You haven't saved any notes in other papers yet.</p>
                            ) : (
                                <ul className="divide-y divide-gray-100">
                                    {importableNotes.map(note => (
                                        <li key={note.id} className="py-3 px-2 hover:bg-gray-50 rounded transition flex items-center justify-between group cursor-pointer" onClick={() => handleImportNote(note)}>
                                            <div>
                                                <p className="text-sm font-bold text-gray-800">{note.lectureTitle || "Untitled Lecture"}</p>
                                                {note.paperId && <p className="text-xs text-gray-500 mt-1">Paper ID: {note.paperId}</p>}
                                            </div>
                                            <span className="text-blue-600 text-sm font-bold opacity-0 group-hover:opacity-100 transition">Import &rarr;</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
export default LectureRoom;
